import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import VillasPageAmenitiesModalContent from 'components/OfferPage/Villas/VillasPageAmenitiesModalContent'
import ModalContext from 'contexts/ModalContext'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import OfferCardCapacityItems, { OfferCardCapacityGrid } from './OfferCardCapacityItems'

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
  offerUrl: string
  totalInclusions?: number
}

function VillaCardInclusions(props: Props) {
  const {
    offer,
    offerUrl,
    totalInclusions = 4,
  } = props

  const intl = useIntl()

  const amenities: Array<App.PackageRoomAmenity> = offer.lowestPricePackage?.roomType.amenities ?? EmptyArray
  const numberOfBedrooms = offer.property.bedrooms
  const numberOfBathrooms = offer.property.bathrooms
  const capacity = useMemo((): number => getMaxGuestCount(offer.lowestPricePackage?.roomRate?.capacities), [offer.lowestPricePackage?.roomRate?.capacities])

  const showModal = useContext(ModalContext)
  const openAmenitiesModal = useCallback((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    showModal(
      <Modal
        title={intl.formatMessage({
          id: 'BgqAPht5p4dbjRTwr2iv',
          defaultMessage: 'Amenities',
          description: 'Title for villa amenities modal on the villa or rental property page',
        })}
        primaryActionTo={offerUrl}
        primaryActionText={intl.formatMessage({
          id: 'Bmpg6g4qA5VcilFC-Iaa',
          defaultMessage: 'View offer',
          description: 'Primary action button text in villa amenities modal',
        })}
      >
        <VillasPageAmenitiesModalContent amenities={amenities} />
      </Modal>,
    )
  }, [showModal, offerUrl, amenities, intl])

  // numberOfBedrooms, numberOfBathrooms and capacity are already shown, so we want the other amenities
  // NOTE: We're taking only 3 amenities because we show at most 6 on hero tile
  const otherAmenities = useMemo(() => take(amenities, totalInclusions - 3), [totalInclusions, amenities])

  return (<VerticalSpacer gap={8}>
    <OfferCardCapacityGrid>
      <OfferCardCapacityItems guests={capacity} bedrooms={numberOfBedrooms} bathrooms={numberOfBathrooms} />
      {otherAmenities.map((amenity) => <OfferInclusionItem
        key={amenity.id}
        inclusion={amenity}
      />)}
    </OfferCardCapacityGrid>
    {amenities.length > 1 && <TextLink size="medium" onClick={openAmenitiesModal}>
      {intl.formatMessage(
        {
          id: 'xbOzUemkhN0lWXOmy_BW',
          defaultMessage: 'Show all {count} amenities',
          description: 'Hyperlink text to show all villa amenities in a modal',
        },
        { count: amenities.length },
      )}
    </TextLink>}
  </VerticalSpacer>)
}

export default VillaCardInclusions
